/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { getStringWidth } from '@visx/text'
import { useCallback, useEffect, useRef, useState } from 'react'
import { sortTags } from 'lib/sortTags'

const FONT_CONFIG = {
  fontSize: `14px`,
  lineHeight: `19.6px`,
  fontWeight: 400,
  fontFamily: `Inter, sans-serif`,
} as const

const TAG_BASE_PADDING = 20
const ICON_WIDTH = 16
const TAGS_WITH_ICONS = [`Emergency`, `AI`] as const

const DEFAULT_MAX_LINES = 1
const DEFAULT_RESERVED_WIDTH = 0
const DEFAULT_BADGE_WIDTH = 35
const DEFAULT_GAP_SIZE = 6

type UseVisibleTagsOptions = {
  tags: string[]
  containerRef: React.RefObject<HTMLElement | null>
  maxLines?: number
  reservedWidth?: number
  badgeWidth?: number
  gapSize?: number
  sortTags?: boolean
}
/**
 * A custom hook that calculates which tags should be visible within a container based on available width.
 * The hook handles both single-line and multi-line layouts, with special handling for emergency tags
 * and tags with icons. It also provides a badge count for hidden tags when space is limited.
 *
 * @param options - Configuration options for the hook
 * @param options.tags - Array of tag strings to display
 * @param options.containerRef - React ref to the container element that holds the tags
 * @param options.maxLines - Maximum number of lines to display tags on (default: 1)
 * @param options.reservedWidth - Width to reserve for other elements in the container (default: 0)
 * @param options.badgeWidth - Width reserved for the "+X" badge when tags are hidden (default: 35)
 * @param options.gapSize - Gap size between tags in pixels (default: 6)
 * @param options.sortTags - Whether to sort the visible tags using the sortTags utility (default: true)
 *
 * @returns Object containing:
 * @returns visibleTags - Array of tags that fit within the available space
 * @returns hiddenTags - Array of tags that don't fit and are hidden
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null)
 * const { visibleTags, hiddenTags } = useVisibleTags({
 *   tags: ['Emergency', 'AI', 'Follow-up'],
 *   containerRef,
 *   maxLines: 2,
 *   reservedWidth: 100
 * })
 *
 * return (
 *   <div ref={containerRef}>
 *     {visibleTags.map(tag => <Tag key={tag}>{tag}</Tag>)}
 *     {hiddenTags.length > 0 && <Badge>+{hiddenTags.length}</Badge>}
 *   </div>
 * )
 * ```
 */
export function useVisibleTags({
  tags,
  containerRef,
  maxLines = DEFAULT_MAX_LINES,
  reservedWidth = DEFAULT_RESERVED_WIDTH,
  badgeWidth = DEFAULT_BADGE_WIDTH,
  gapSize = DEFAULT_GAP_SIZE,
  sortTags: shouldSortTags = true,
}: UseVisibleTagsOptions) {
  const [visibleTags, setVisibleTags] = useState<string[]>([])
  const [hiddenTags, setHiddenTags] = useState<string[]>([])
  const observerRef = useRef<ResizeObserver>(undefined)

  const getEstimatedWidth = (tag: string) => {
    const textWidth = getStringWidth(tag, FONT_CONFIG) ?? 0
    let totalWidth = textWidth + TAG_BASE_PADDING

    if (TAGS_WITH_ICONS.includes(tag as any) || tag.toUpperCase() === `AI`) {
      totalWidth += ICON_WIDTH
    }

    return totalWidth
  }

  const updateVisibleTags = useCallback(() => {
    if (!containerRef.current) return

    const containerWidth = containerRef.current.offsetWidth - reservedWidth
    let visible: string[] = []

    const emergencyTag = tags.find((tag) => tag === `Emergency`)
    const otherTags = tags.filter((tag) => tag !== `Emergency`)
    const allTagsToProcess = emergencyTag ? [emergencyTag, ...otherTags] : otherTags

    if (maxLines === 1) {
      let testWidth = 0
      let willNeedBadge = false

      for (const tag of allTagsToProcess) {
        const tagWidth = getEstimatedWidth(tag)

        if (testWidth > 0 && testWidth + tagWidth + gapSize > containerWidth) {
          willNeedBadge = true
          break
        } else {
          testWidth += (testWidth > 0 ? gapSize : 0) + tagWidth
        }
      }

      const effectiveWidth = willNeedBadge ? containerWidth - badgeWidth : containerWidth
      let currentLineWidth = 0

      for (const tag of allTagsToProcess) {
        const tagWidth = getEstimatedWidth(tag)

        if (currentLineWidth > 0 && currentLineWidth + tagWidth + gapSize > effectiveWidth) {
          break
        }

        visible.push(tag)
        currentLineWidth += (currentLineWidth > 0 ? gapSize : 0) + tagWidth
      }
    } else {
      let testWidth = 0
      let testLine = 1
      let willNeedBadge = false

      for (const tag of allTagsToProcess) {
        const tagWidth = getEstimatedWidth(tag)

        if (testWidth > 0 && testWidth + tagWidth + gapSize > containerWidth) {
          testLine++
          testWidth = tagWidth
        } else {
          testWidth += (testWidth > 0 ? gapSize : 0) + tagWidth
        }

        if (testLine > maxLines) {
          willNeedBadge = true
          break
        }
      }

      const effectiveWidth = willNeedBadge ? containerWidth - badgeWidth : containerWidth
      let currentLineWidth = 0
      let currentLine = 1

      for (const tag of allTagsToProcess) {
        const tagWidth = getEstimatedWidth(tag)

        if (currentLineWidth > 0 && currentLineWidth + tagWidth + gapSize > effectiveWidth) {
          currentLine++
          currentLineWidth = 0

          if (currentLine > maxLines) {
            break
          }
        }

        visible.push(tag)
        currentLineWidth += (currentLineWidth > 0 ? gapSize : 0) + tagWidth
      }
    }

    setVisibleTags(shouldSortTags ? sortTags(visible) : visible)
    setHiddenTags(tags.filter((tag) => !visible.includes(tag)))
  }, [tags, reservedWidth, maxLines, badgeWidth, gapSize, shouldSortTags])

  useEffect(() => {
    updateVisibleTags()
  }, [updateVisibleTags])

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      updateVisibleTags()
    })

    if (containerRef.current) {
      observerRef.current?.disconnect()
      resizeObserver.observe(containerRef.current)
      observerRef.current = resizeObserver
    }

    return () => {
      observerRef.current?.disconnect()
    }
  }, [containerRef.current, updateVisibleTags])

  return { visibleTags, hiddenTags }
}
